import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { 
  Radio,
  MessageCircle,
  Globe,
  Users,
  Briefcase,
  ArrowRight,
  Loader2,
  FileText,
  Layers,
  Sparkles
} from 'lucide-react';
import { StoryFormatCard } from '../../components/admin/StoryFormatCard';
import { GeneratedOutputCard } from '../../components/admin/outputs/GeneratedOutputCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const channelIcons = {
  radio: Radio,
  whatsapp: MessageCircle,
  digital: Globe,
  community: Users,
  policy: Briefcase,
};

const channelColors = {
  radio: 'bg-orange-100 text-orange-800 border-orange-200',
  whatsapp: 'bg-green-100 text-green-800 border-green-200',
  digital: 'bg-blue-100 text-blue-800 border-blue-200',
  community: 'bg-purple-100 text-purple-800 border-purple-200',
  policy: 'bg-gray-100 text-gray-800 border-gray-200',
};

// Format examples for each channel
const formatExamples = {
  radio: {
    title: "Radio Diary Format",
    example: `[INTRO MUSIC - 5 seconds]

HOST: Welcome to YEFFA Stories. Today we hear from {author_name} in {country_name}.

[STORY BEGINS]
"{caption}"

{full_content_excerpt}

[OUTRO]
HOST: That was {author_name}'s story. If you have a story to share, visit yeffa.org.

[MUSIC FADE - 3 seconds]`,
  },
  whatsapp: {
    title: "WhatsApp Broadcast Format",
    example: `🌱 *YEFFA Youth Story*

📍 {country_name}

"{caption}"

👤 {author_name}

🔗 Read full story: [link]

#YEFFAStories #{theme}`,
  },
  digital: {
    title: "Social Media Post Format",
    example: `Meet {author_name} from {country_name} 🌍

"{caption}"

Their journey shows us what's possible when young farmers are supported.

Read the full story 👇
[link]

#YEFFAStories #YouthInAgriculture #{country_name} #{theme}`,
  },
  community: {
    title: "Town Hall Presentation Format",
    example: `STORY SHOWCASE: {title}

Speaker: {author_name}, {country_name}

Key Points:
• Personal journey and challenges faced
• Solutions implemented
• Impact on community
• Call to action for attendees

Discussion Questions:
1. What resonates with your own experience?
2. How can we support similar initiatives locally?`,
  },
  policy: {
    title: "Evidence Brief Format",
    example: `YEFFA EVIDENCE BRIEF

Case Study: {title}
Location: {country_name}
Theme: {theme}

SUMMARY
{caption}

KEY FINDINGS
• [Data point 1]
• [Data point 2]

POLICY IMPLICATIONS
This story demonstrates the need for...

RECOMMENDATIONS
1. [Policy recommendation]
2. [Investment priority]`,
  },
};

// Output Channel Card
const OutputChannelCard = ({ channel, onAssignStory }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = channelIcons[channel.name] || FileText;
  const colorClass = channelColors[channel.name] || 'bg-gray-100 text-gray-800';
  const format = formatExamples[channel.name];

  return (
    <Card className={`border-2 ${channelColors[channel.name]?.split(' ')[2] || 'border-gray-200'}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClass}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <CardTitle className="text-lg">{channel.display_name}</CardTitle>
              <p className="text-sm text-gray-600">{channel.description}</p>
            </div>
          </div>
          <Badge className={colorClass}>
            {channel.story_count || 0} stories
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="w-full justify-between text-sm"
        >
          {expanded ? 'Hide Format Template' : 'View Format Template'}
          <ArrowRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </Button>

        {expanded && format && (
          <div className="mt-4 bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">{format.title}</h4>
            <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono bg-white p-3 rounded border border-gray-200">
              {format.example}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Outputs Page
export const OutputsPage = () => {
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('channels');
  const [formatStories, setFormatStories] = useState([]);
  const [formatLoading, setFormatLoading] = useState(false);
  const [countryFilter, setCountryFilter] = useState('all');
  const [outputsStories, setOutputsStories] = useState([]);
  const [outputsLoading, setOutputsLoading] = useState(false);
  const [outputsCountryFilter, setOutputsCountryFilter] = useState('all');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/login', { state: { from: { pathname: '/admin/outputs' } } });
      return;
    }
    fetchOutputs();
    fetchFormatStories();
  }, [isAdmin, navigate]);

  const fetchOutputs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/outputs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChannels(data);
      }
    } catch (error) {
      console.error('Failed to fetch outputs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormatStories = async () => {
    if (formatStories.length > 0) return;
    setFormatLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/stories-with-formats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setFormatStories(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch format stories:', error);
    } finally {
      setFormatLoading(false);
    }
  };

  const fetchOutputsStories = async () => {
    setOutputsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/stories-with-outputs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setOutputsStories(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch outputs stories:', error);
    } finally {
      setOutputsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'formats' && isAdmin) {
      fetchFormatStories();
    }
    if (tab === 'generated' && isAdmin) {
      fetchOutputsStories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#07743F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Where Stories Go</h1>
          <p className="text-sm text-gray-600">
            Output channels and ready-to-use multi-format assets for distribution
          </p>
          <div className="flex gap-2 mt-3 border-b border-transparent">
            <button
              type="button"
              data-testid="outputs-tab-channels"
              onClick={() => setTab('channels')}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition ${
                tab === 'channels'
                  ? 'border-[#07743F] text-[#07743F]'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              <Globe className="w-4 h-4" /> Channels
            </button>
            <button
              type="button"
              data-testid="outputs-tab-formats"
              onClick={() => setTab('formats')}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition ${
                tab === 'formats'
                  ? 'border-[#07743F] text-[#07743F]'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              <Layers className="w-4 h-4" /> Story Formats
              <span className="text-xs bg-[#E4E517] text-gray-900 px-1.5 py-0.5 rounded">
                {formatStories.length || '·'}
              </span>
            </button>
            <button
              type="button"
              data-testid="outputs-tab-generated"
              onClick={() => setTab('generated')}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition ${
                tab === 'generated'
                  ? 'border-[#07743F] text-[#07743F]'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              <Sparkles className="w-4 h-4" /> Generated Outputs
              <span className="text-xs bg-[#07743F] text-white px-1.5 py-0.5 rounded">
                {outputsStories.length || '·'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {tab === 'channels' && (
        <>
          {/* Flow Diagram */}
          <div className="bg-gradient-to-r from-[#07743F] to-[#72BF44] py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-4 text-white overflow-x-auto">
            <div className="text-center flex-shrink-0">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <FileText className="w-6 h-6" />
              </div>
              <span className="text-sm font-medium">Raw Story</span>
            </div>
            <ArrowRight className="w-6 h-6 flex-shrink-0" />
            <div className="text-center flex-shrink-0">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-lg">⚙️</span>
              </div>
              <span className="text-sm font-medium">Format</span>
            </div>
            <ArrowRight className="w-6 h-6 flex-shrink-0" />
            <div className="text-center flex-shrink-0">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-lg">📡</span>
              </div>
              <span className="text-sm font-medium">Distribute</span>
            </div>
            <ArrowRight className="w-6 h-6 flex-shrink-0" />
            <div className="text-center flex-shrink-0">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-lg">📊</span>
              </div>
              <span className="text-sm font-medium">Impact</span>
            </div>
          </div>
        </div>
      </div>

      {/* Channel Cards */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {channels.map(channel => (
            <OutputChannelCard key={channel.id} channel={channel} />
          ))}
        </div>
      </div>

      {/* Info Card */}
      <div className="container mx-auto px-4 pb-8">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <h3 className="font-bold text-amber-900 mb-2">How Story Distribution Works</h3>
            <p className="text-amber-800 text-sm">
              Once a story is approved and published, it can be assigned to one or more output channels. 
              Each channel has a specific format template that transforms the raw story into channel-appropriate content. 
              Stories marked as "Used" have been distributed through at least one channel.
            </p>
          </CardContent>
        </Card>
      </div>
        </>
      )}

      {tab === 'formats' && (
        <div className="container mx-auto px-4 py-8">
          <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-serif font-bold text-gray-900">
                Multi-Format Story Assets
              </h2>
              <p className="text-sm text-gray-600 max-w-2xl">
                Every expanded story is broken down into ready-to-publish formats:
                radio diary scripts, WhatsApp broadcasts, social bursts, and shareable quotes.
                Copy any block and ship it.
              </p>
            </div>
            {formatStories.length > 0 && (
              <select
                data-testid="format-country-filter"
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="all">All countries ({formatStories.length})</option>
                {Array.from(new Set(formatStories.map((s) => s.country_name))).sort().map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
          </div>

          {formatLoading && (
            <div className="flex items-center gap-2 text-gray-500 py-8">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading expanded stories…
            </div>
          )}

          {!formatLoading && formatStories.length === 0 && (
            <Card className="bg-gray-50 border-dashed">
              <CardContent className="p-8 text-center text-gray-500">
                No expanded stories yet. Stories with narrative expansion fields
                (core tension, posture shift, archetype) will appear here as
                ready-to-distribute multi-format assets.
              </CardContent>
            </Card>
          )}

          {!formatLoading && formatStories.length > 0 && (
            <div className="grid lg:grid-cols-2 gap-5" data-testid="story-formats-grid">
              {formatStories
                .filter((s) => countryFilter === 'all' || s.country_name === countryFilter)
                .map((s) => (
                  <StoryFormatCard key={s.id} story={s} token={token} />
                ))}
            </div>
          )}
        </div>
      )}

      {tab === 'generated' && (
        <div className="container mx-auto px-4 py-8">
          <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-serif font-bold text-gray-900">
                Decentralised Storytelling Engine — Generated Outputs
              </h2>
              <p className="text-sm text-gray-600 max-w-3xl">
                Every approved story is automatically transformed into 15–20 campaign-ready
                assets: WhatsApp variants, radio diaries, social carousels, video scripts,
                policy briefs, media summaries, and structured evidence. Send any output to
                a channel queue with one click.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {outputsStories.length > 0 && (
                <select
                  data-testid="outputs-country-filter"
                  value={outputsCountryFilter}
                  onChange={(e) => setOutputsCountryFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="all">All countries ({outputsStories.length})</option>
                  {Array.from(new Set(outputsStories.map((s) => s.country_name))).sort().map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              )}
              <button
                type="button"
                data-testid="refresh-outputs"
                onClick={fetchOutputsStories}
                className="text-xs flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 hover:border-[#07743F] hover:text-[#07743F] transition"
              >
                <Loader2 className={`w-3 h-3 ${outputsLoading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
          </div>

          {outputsLoading && outputsStories.length === 0 && (
            <div className="flex items-center gap-2 text-gray-500 py-8">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading approved stories…
            </div>
          )}

          {!outputsLoading && outputsStories.length === 0 && (
            <Card className="bg-gray-50 border-dashed">
              <CardContent className="p-8 text-center text-gray-500">
                No approved stories yet. Approve a story from the Pipeline and outputs will
                appear here automatically.
              </CardContent>
            </Card>
          )}

          {outputsStories.length > 0 && (
            <div className="grid lg:grid-cols-2 gap-5" data-testid="generated-outputs-grid">
              {outputsStories
                .filter((s) => outputsCountryFilter === 'all' || s.country_name === outputsCountryFilter)
                .map((s) => (
                  <GeneratedOutputCard
                    key={s.id}
                    story={s}
                    token={token}
                    onRefresh={fetchOutputsStories}
                  />
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OutputsPage;
